import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, CalendarDays, MapPin } from 'lucide-react';
import { usePageMeta } from '@/hooks/usePageMeta';
import PageShell, { PageHero, LightSection } from '@/components/PageShell';
import { FundTheGapCTA } from '@/components/content/FundTheGapCTA';
import { ArticleFeedback } from '@/components/content/ArticleFeedback';
import { TripBudgetCalculator } from '@/components/content/TripBudgetCalculator';
import { CHAMPIONSHIPS_LAST_VERIFIED, championships, sportLabel, type Championship } from '@/lib/data/championships';
import { sportGuides } from '@/lib/data/sportGuides';
import { cn } from '@/lib/utils';

const TITLE = 'Road to Nationals: US Youth Championship Calendar 2026–27';
const DESCRIPTION =
  'Dates, host cities and qualifying routes for US youth and amateur national championships in 2026–27 — plus a trip budget calculator for families who just qualified.';

const MONTH_FMT = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });

/** yyyy-mm of the event's last day (or its usual month), for "still upcoming" checks. */
function lastMonth(c: Championship): string {
  return (c.end ?? c.start ?? c.month).slice(0, 7);
}

function monthHeading(yyyyMm: string): string {
  return MONTH_FMT.format(new Date(`${yyyyMm}-01T00:00:00Z`));
}

export default function NationalChampionshipsCalendar() {
  const upcoming = useMemo(() => {
    const thisMonth = new Date().toISOString().slice(0, 7);
    return championships
      .filter((c) => lastMonth(c) >= thisMonth)
      .sort((a, b) => (a.start ?? a.month).localeCompare(b.start ?? b.month));
  }, []);

  usePageMeta(TITLE, DESCRIPTION, {
    article: true,
    image: '/resources/athlete-celebration.jpg',
    breadcrumbs: [{ name: 'Resources', path: '/resources' }, { name: 'National Championships Calendar' }],
    // Only events with published dates become schema.org Events — a "typically
    // July" placeholder is not an event date and must not be marked up as one.
    jsonLd: upcoming
      .filter((c) => c.start)
      .map((c) => ({
        '@context': 'https://schema.org',
        '@type': 'SportsEvent',
        name: c.name,
        startDate: c.start,
        ...(c.end ? { endDate: c.end } : {}),
        eventStatus: 'https://schema.org/EventScheduled',
        eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
        location: { '@type': 'Place', name: c.location, address: c.location },
        organizer: { '@type': 'Organization', name: c.organizer },
        sport: sportLabel(c.sport),
        url: c.url,
      })),
  });

  const sports = useMemo(() => [...new Set(upcoming.map((c) => c.sport))].sort(), [upcoming]);
  const [sport, setSport] = useState<string>('all');
  const visible = upcoming.filter((c) => sport === 'all' || c.sport === sport);

  const byMonth = useMemo(() => {
    const groups = new Map<string, Championship[]>();
    for (const c of visible) {
      const key = (c.start ?? c.month).slice(0, 7);
      groups.set(key, [...(groups.get(key) ?? []), c]);
    }
    return [...groups.entries()];
  }, [visible]);

  const guideFor = (s: string) => sportGuides.find((g) => g.sport === s);

  return (
    <PageShell
      hero={
        <PageHero
          eyebrow="Road to Nationals · Calendar"
          title={
            <>
              Qualified for nationals?{' '}
              <span className="bg-gradient-to-r from-yellow-300 via-yellow-400 to-amber-300 bg-clip-text text-transparent">
                Here's the calendar — and the trip math.
              </span>
            </>
          }
          lead="The US youth and amateur national championships for the 2026–27 season: when, where, and how athletes qualify. Making nationals is the best news of the season — and usually the most expensive."
          size="sm"
        />
      }
    >
      <LightSection>
        <div className="max-w-4xl">
          <p className="text-slate-600 leading-relaxed">
            A qualifying result often lands only weeks before the event, and the trip — flights, hotel nights, entry
            fees, a parent's time off — arrives all at once. Use the calendar to see what's coming in your sport, and
            the budget calculator below to turn the trip into a number you can share with the people who want to help.
          </p>
          <p className="mt-3 text-sm text-slate-500">
            Last checked {CHAMPIONSHIPS_LAST_VERIFIED} against each organizer's site. "Dates TBA" means the organizer
            hasn't published them yet — confirm on the official page before booking anything.
          </p>
        </div>

        <div className="mt-10 flex flex-wrap gap-2" role="group" aria-label="Filter by sport">
          {['all', ...sports].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setSport(s)}
              aria-pressed={sport === s}
              className={cn(
                'rounded-full border px-4 py-2 text-sm font-medium transition-colors',
                sport === s
                  ? 'border-slate-900 bg-slate-900 text-white'
                  : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400',
              )}
            >
              {s === 'all' ? 'All sports' : sportLabel(s)}
            </button>
          ))}
        </div>

        <div className="mt-10 space-y-12">
          {byMonth.map(([month, events]) => (
            <section key={month} aria-labelledby={`m-${month}`}>
              <h2 id={`m-${month}`} className="text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900">
                {monthHeading(month)}
              </h2>
              <div className="mt-5 grid md:grid-cols-2 gap-5">
                {events.map((c) => {
                  const guide = guideFor(c.sport);
                  return (
                    <article key={c.name} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm flex flex-col">
                      <div className="flex items-center gap-2">
                        <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700">
                          {sportLabel(c.sport)}
                        </span>
                        {!c.start && (
                          <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">
                            Dates TBA
                          </span>
                        )}
                      </div>
                      <h3 className="mt-3 text-lg font-bold text-slate-900 leading-snug">{c.name}</h3>
                      <p className="text-sm text-slate-500">{c.organizer}</p>
                      <ul className="mt-4 space-y-1.5 text-sm text-slate-700">
                        <li className="flex gap-2">
                          <CalendarDays className="h-4 w-4 mt-0.5 shrink-0 text-slate-400" aria-hidden />
                          {c.when}
                        </li>
                        <li className="flex gap-2">
                          <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-slate-400" aria-hidden />
                          {c.location}
                        </li>
                      </ul>
                      <p className="mt-3 text-sm text-slate-600">
                        <span className="font-semibold text-slate-900">Who: </span>
                        {c.level}
                      </p>
                      <p className="mt-1 text-sm text-slate-600">
                        <span className="font-semibold text-slate-900">How to qualify: </span>
                        {c.qualify}
                      </p>
                      <div className="mt-auto pt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm font-semibold">
                        <a href={c.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-700">
                          Official page <ArrowUpRight className="h-4 w-4" />
                        </a>
                        {guide && (
                          <Link to={`/resources/fundraising-guides/${guide.slug}`} className="text-slate-700 hover:text-blue-700">
                            Fund a {guide.name.toLowerCase()} season →
                          </Link>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          ))}
          {byMonth.length === 0 && <p className="text-slate-500">No upcoming events listed for that sport yet.</p>}
        </div>

        <div className="max-w-3xl">
          <h2 id="trip-budget" className="mt-16 text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900">
            Nationals trip budget calculator
          </h2>
          <p className="mt-2 text-slate-600">
            Plug in the trip. It gives you a total, and what that total looks like spread across your supporters — the
            number to put on a fund page.
          </p>
          <TripBudgetCalculator />

          <FundTheGapCTA
            title="Just qualified? Share the trip, not the stress."
            body="Put the trip budget on a free fund page and send it to the family group chat. Supporters can give once or monthly, 100% of every donation reaches your athlete (the small fee is added on top for the supporter), and payouts go straight to your family's bank."
          />

          <h2 className="mt-12 text-2xl font-extrabold tracking-tight text-slate-900">Missing a championship?</h2>
          <p className="mt-2 text-slate-600">
            We add events as organizers publish dates. If your sport's national championship isn't here,{' '}
            <Link to="/contact" className="text-blue-700 underline">
              tell us
            </Link>{' '}
            and we'll check it and add it.
          </p>
          <ArticleFeedback articleSlug="national-championships-calendar" />
        </div>
      </LightSection>
    </PageShell>
  );
}
